import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControlLabel,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  StepIcon,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  styled,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import ErrorIcon from '@mui/icons-material/Error';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import DataGrid from 'react-data-grid';
import { format } from 'date-fns';

import {
  validateFile,
  parseXLSXFile,
  validateAllRows,
  normalizeParsedRows,
} from './GDOValidationEngine';
import { createEmptyRefData, fetchExistingGDONames, fetchLookupData } from './GDOReferenceData';
import { GDOValidationPreviewTable } from './GDOValidationPreviewTable';
import { GDOStatusSummary } from './GDOStatusSummary';
import { generateStatusReport, generateInvalidEntriesTemplate } from './GDOStatusReportGenerator';
import {
  GBulkUploadModalProps,
  ModalStep,
  RawGDORow,
  ValidatedGDORow,
  UploadSummary,
  DisplayRow,
  ValidationRefData,
} from './GDOBulkUploadTypes';
import {
  COLUMN_KEYS,
  COLUMN_HEADERS,
  COLUMN_WIDTHS,
  getColumnComment,
  getSampleValues,
  getColumnWidths,
} from './GDOBulkUploadTypes';
import { IBP_TEMPLATE } from './ibp-template';
import { bulkUploadDataPipelineEntities } from '../../service/service';

const StyledStepIcon = styled('div')(({ active, completed }: { active: boolean; completed?: boolean }) => ({
  width: '34px',
  height: '34px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  backgroundColor: active || completed ? '#EFF6FF' : '#71758F',
  color: '#003771',
  fontWeight: 'bold',
  transition: 'all 0.3s ease',
  '& svg path': {
    fill: '#003771',
  },
}));

interface StepIconProps {
  active: boolean;
  completed: boolean;
  index: number;
}

function StepIconForward({ active, completed, index }: StepIconProps) {
  return completed ? (
    <StyledStepIcon active={active} completed>
      <CheckIcon />
    </StyledStepIcon>
  ) : (
    <StyledStepIcon active={active}>{index + 1}</StyledStepIcon>
  );
}

function getStepIconComponent(index: number, activeStep: number) {
  return function StepIcon(props: any) {
    return (
      <StepIconForward
        active={props.active}
        completed={activeStep > index}
        index={index}
      />
    );
  };
}

const StyledConnector = styled(StepConnector)(({ completed }: { completed: boolean }) => ({
  '&.MuiStepConnector-root': {
    top: 22,
    width: '50px',
    flex: 'none',
  },
  '& .MuiStepConnector-line': {
    borderColor: completed ? '#EFF6FF' : '#71758F',
    borderTopWidth: 2,
    transition: 'all 0.3s ease',
  },
}));

const StyledLabel = styled(Typography)(({ active, completed }: { active: boolean; completed?: boolean }) => {
  let colorValue = active || completed ? '#EFF6FF' : '#71758F';
  return {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: colorValue,
    transition: 'all 0.3s ease',
  };
});

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const steps = ['Upload File', 'Confirm & Add'];

// Theme color constants (matching button/border colors)
const THEME_PRIMARY = '#003771'; // Button background color
const THEME_ACCENT = '#009BD8'; // Border/accent color

// CSS keyframes for spinner animation
const spinKeyframes = `
@keyframes ibp-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
@keyframes ibp-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
`;

// Inject keyframes once
if (typeof document !== 'undefined' && !document.getElementById('ibp-loader-keyframes')) {
  const style = document.createElement('style');
  style.id = 'ibp-loader-keyframes';
  style.textContent = spinKeyframes;
  document.head.appendChild(style);
}

// Reusable CSS Loader component with theme color
interface CSSLoaderProps {
  size?: number;
  thickness?: number;
  message?: string;
  subMessage?: string;
}

const CSSLoader: React.FC<CSSLoaderProps> = ({
  size = 64,
  thickness = 4,
  message = 'Processing...',
  subMessage = 'Please wait for a while...',
}) => (
  <Box
    sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      minHeight: 300,
    }}
  >
    {/* Spinner */}
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: `${thickness}px solid ${THEME_ACCENT}33`,
        borderTopColor: THEME_PRIMARY,
        animation: 'ibp-spin 1s linear infinite',
      }}
    />
    {/* Main message */}
    <Typography
      sx={{
        fontWeight: 600,
        color: THEME_PRIMARY,
        fontSize: '16px',
        textAlign: 'center',
        animation: 'ibp-pulse 2s ease-in-out infinite',
      }}
    >
      {message}
    </Typography>
    {/* Sub message */}
    {subMessage && (
      <Typography
        sx={{
          color: '#78818c',
          fontSize: '14px',
          textAlign: 'center',
          maxWidth: 400,
        }}
      >
        {subMessage}
      </Typography>
    )}
  </Box>
);

export const GBulkUploadModal: React.FC<GBulkUploadModalProps> = ({ open, onClose }) => {
  const [activeStep, setActiveStep] = useState<ModalStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [rawRows, setRawRows] = useState<RawGDORow[]>([]);
  const [validatedRows, setValidatedRows] = useState<ValidatedGDORow[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<number>>(new Set());
  const [showValidOnly, setShowValidOnly] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [uploadSummary, setUploadSummary] = useState<UploadSummary | null>(null);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refData, setRefData] = useState<ValidationRefData>(createEmptyRefData);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open && activeStep === 'upload') {
      // Fetch lookup data (sectors, markets, reports) from DB and existing GDO names in parallel
      Promise.all([
        fetchLookupData(),
        fetchExistingGDONames(),
      ]).then(([lookupData, names]) => {
        setRefData((prev) => ({
          ...prev,
          sectors: lookupData.sectors.length > 0 ? lookupData.sectors : prev.sectors,
          markets: lookupData.markets.length > 0 ? lookupData.markets : prev.markets,
          reports: lookupData.reports.length > 0 ? lookupData.reports : prev.reports,
          existingGDONames: names,
          platformSchedulerMap: Object.keys(lookupData.platformSchedulerMap).length > 0 ? lookupData.platformSchedulerMap : prev.platformSchedulerMap,
        }));
      });
    }
  }, [open, activeStep]);

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [onClose]);

  const handleReset = useCallback(() => {
    setActiveStep('upload');
    setFile(null);
    setRawRows([]);
    setValidatedRows([]);
    setSelectedRowIds(new Set());
    setShowValidOnly(false);
    setFileErrors([]);
    setUploadSummary(null);
    setIsLoading(false);
    setUploading(false);
    setUploadProgress('');
    setConfirmOpen(false);
  }, []);

  const handleDialogClose = (_event: {}, _reason: 'backdropClick' | 'escapeKeyDown') => {
    handleClose();
  };

const handleDownloadTemplate = useCallback(async () => {
    // Ensure lookup data is loaded before generating template
    let sectors = refData.sectors;
    let markets = refData.markets;
    let reports = refData.reports;
    if (sectors.length === 0 || markets.length === 0 || reports.length === 0) {
      try {
        const lookup = await fetchLookupData();
        sectors = lookup.sectors;
        markets = lookup.markets;
        reports = lookup.reports;
        setRefData((prev) => ({ ...prev, ...lookup }));
      } catch (e) {
        console.warn('Failed to fetch lookup data for template:', e);
      }
    }
    const sectorList = sectors.join(', ') || '(none available)';
    const marketList = markets.join(', ') || '(none available)';
    const reportList = reports.join(', ') || '(none available)';
    const workbook = XLSX.utils.book_new();

    const titleRow = [`GDO Bulk Upload Template — Generated: ${new Date().toLocaleString()}`];
    const emptyRow = [''];
    // Exclude remarks column from template (remarks is system-populated, only shown in error sheet)
    const userColumnKeys = COLUMN_KEYS.filter((key) => key !== 'remarks');
    const headers = [...userColumnKeys.map((k) => COLUMN_HEADERS[k])];
    
    // Generate comments from template configuration
    const refDataForComments = { sectors, markets, reports, platformSchedulerMap: refData.platformSchedulerMap };
    const notes = userColumnKeys.map((key) => getColumnComment(key, refDataForComments));

    // Generate sample rows from template configuration (exclude remarks column)
    const sampleRows: string[][] = [];
    for (let i = 0; i < 3; i++) {
      const row: string[] = [];
      for (const col of IBP_TEMPLATE.columns) {
        // Skip remarks column in template
        if (col.key === 'remarks') continue;
        if (col.key === 'gdoName') {
          // Show alphanumeric pattern: GDO-001, GDO-A1B, GDO-ABC
          const gdoExamples = ['GDO-001', 'GDO-A1B', 'GDO-ABC'];
          row.push(gdoExamples[i] || `GDO-${String(i + 1).padStart(3, '0')}`);
        } else if (col.key === 'sector') {
          row.push(sectors[i] || ['PBNA', 'PFNA', 'LATAM'][i] || '');
        } else if (col.key === 'market') {
          row.push(markets[i] || ['BNL', 'FRANCE', 'BENELUX'][i] || '');
        } else if (col.key === 'report') {
          row.push(reports[i] || ['iS&OE', 'Demand Plan', 'Supply Plan'][i] || '');
        } else if (col.key === 'origin') {
          row.push(['o9', 'SAP', 'EDW'][i] || '');
        } else if (col.key === 'destination') {
          row.push(['Snowflake', 'ADW', 'o9'][i] || '');
        } else if (col.key === 'timezone') {
          row.push('CST');
        } else if (col.key === 'owner') {
          row.push(['John Doe', 'Jane Smith', 'Bob Wilson'][i] || '');
        } else if (col.key === 'gdoDescription') {
          row.push(['Supply data load for region', 'Demand plan data transfer', 'PFNA sales data refresh'][i] || '');
        } else if (col.key === 'gdoCategory') {
          row.push(['Data Asset', 'Analytics', 'Reporting'][i] || '');
        } else if (col.key === 'platform') {
          row.push(['ADF', 'BODS', 'Teradata'][i] || '');
        } else if (col.key === 'schedulerName') {
          row.push(['ADF', 'Control-M-PGT', 'HWA'][i] || '');
        } else if (col.key === 'jobName') {
          row.push(['GDO_PBNA_Daily_Load', 'ADF_PFNA_Extract', 'BODS_EMEA_Transform'][i] || '');
        } else if (col.key === 'scheduleDays') {
          row.push(['Monday,Wednesday,Friday', 'Tuesday,Thursday', 'Monday,Tuesday,Wednesday,Thursday,Friday'][i] || '');
        } else if (col.key === 'executionStartTime') {
          row.push(['09:00', '14:00', '06:00'][i] || '');
        } else if (col.key === 'executionEndTime') {
          row.push(['11:00', '16:00', '08:00'][i] || '');
        } else if (col.key === 'averageExecutionTime') {
          row.push(['45', '60', '30'][i] || '');
        } else if (col.key === 'loadFrequency') {
          row.push(['Daily', 'Weekly', 'Daily'][i] || '');
        } else if (col.key === 'slaType') {
          row.push(['Daily', 'Weekly', 'Daily'][i] || '');
        } else if (col.key === 'slaDays') {
          row.push(['Mon,Tue,Wed,Thu,Fri', 'Mon,Tue,Wed,Thu,Fri', 'All Days'][i] || '');
        } else if (col.key === 'slaStartTime') {
          row.push(['08:00', '06:00,14:00', '00:00,06:00,12:00,18:00'][i] || '');
        } else if (col.key === 'slaEndTime') {
          row.push(['18:00', '10:00,18:00', '04:00,10:00,16:00,02:00'][i] || '');
        } else if (col.key === 'slaCriteria') {
          row.push(['ANY RUN', 'ALL RUNS', 'SELECTED RUNS'][i] || '');
        } else if (col.key === 'stageInboundOutbound') {
          row.push(['Inbound', 'Outbound', 'Both'][i] || '');
        } else if (col.key === 'gdoStep') {
          row.push([1, 2, 3][i].toString());
        } else if (col.key === 'gdoDependentOn') {
          row.push(i === 1 ? 'GDO-001' : (i === 2 ? 'GDO-001,GDO-A1B' : ''));
        } else if (col.key === 'factoryName') {
          // ADF Factory Name - only for ADF platform (row 1)
          row.push(i === 0 ? 'pepwise-prod-eus2' : '');
        } else if (col.key === 'workspaceName') {
          // Workspace Name - only for ADB platform (none in sample)
          row.push('');
        } else if (col.key === 'application') {
          // Application - only for Control-M schedulers (row 2)
          row.push(i === 1 ? 'IBP_PROD' : '');
        } else if (col.key === 'folderPath') {
          // Folder Path - only for Control-M schedulers (row 2)
          row.push(i === 1 ? 'FOLDER/SUBFOLDER' : '');
        } else {
          row.push('');
        }
      }
      sampleRows.push(row);
    }

    const wsData = [titleRow, emptyRow, headers, ...sampleRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Add comments to header cells (row 3, index 2)
    for (let c = 0; c < headers.length; c++) {
      const cellRef = XLSX.utils.encode_cell({ r: 2, c });
      if (ws[cellRef] && notes[c]) {
        ws[cellRef].c = [{ a: c < 25 ? 'Template' : 'ChatGPT', t: notes[c] }];
      }
    }

    // Column widths from template configuration
    ws['!cols'] = getColumnWidths();
    ws['!freeze'] = { xSplit: 0, ySplit: 3, topLeftCell: 'A4' };

    // Create Instruction Guide worksheet with inline data using backtick strings for proper newlines
    const guideHeaders = ['Section', 'Details', 'Description / Instructions', 'Example / Reference'];
    const guideData = [
      ['1. GDO data Structure', 'File Format', `This workbook contains 2 sheets:\n1. Instruction Guide - This sheet with detailed instructions\n2. GDO data - Main data entry sheet\n\nNote: A Summary & Errors sheet is generated automatically after upload for review.`, 'Download template → Fill data → Upload → Review Summary & Errors sheet'],
      ['2. GDO data Sheet (Main Data Entry)', 'How to Fill', `• Sheet name must be exactly "GDO data" — do not rename it\n• Headers MUST be on row 3 — do not move them up or down\n• Fill data starting from row 4 (row 1 is blank, row 2 is blank, row 3 has headers with hover comments)\n• Mandatory fields marked with * in header\n• Hover over header cells for field descriptions\n• GDO Name must be unique and start with GDO-<alphanumeric> (e.g., GDO-001, GDO-A1B)\n• Do not delete, reorder, or rename columns\n• Do not insert or delete rows above the data (rows 1-3 are reserved)\n• Note: The "Remarks / Error Flag" column is NOT in this template — it only appears in the invalid entries template after upload`, 'GDO-001, GDO-A1B, GDO-ABC — PBNA, BNL, iS&OE, o9, Snowflake, CST, John Doe, ...'],
      ['3. Mandatory Fields (Marked with *)', 'Required Fields', 'GDO Name *, Sector *, Market *, Report *, Origin *, Destination *, Timezone *, Owner *, Platform *, Scheduler Name *, Job Name *, Schedule Info *', 'GDO-001, GDO-A1B, GDO-ABC — PBNA, BNL, iS&OE, o9, Snowflake, CST, John Doe, ...'],
      ['4. Sector & Market Validation', 'Independent Validation', `Sectors and Markets are loaded from database collections (validated independently).\nSectors: ${sectorList}\n\nMarkets: ${marketList}`, 'Sector and Market values must match entries in the database. Case-insensitive.'],
      ['5. Timezone & SLA Fields', 'Timezone & SLA Format', `Timezone: Must be a valid timezone from the list (e.g., CST, EST, PST, UTC, America/New_York, Europe/London, Asia/Tokyo)\nSLA Type: Optional — Daily, Weekly, Monthly, Bi-Weekly, Quarterly\nSLA Days: Optional — "All Days" or comma-separated days (Mon,Tue,Wed...)\nSLA Start/End Time: Optional — HH:MM 24-hr format (CST), End must be after Start. Comma-separate multiple times to create multiple SLA slots in one day for cyclic jobs (e.g., 06:00,14:00 paired with 10:00,18:00) — slots pair by position, use the same count in both columns\nSLA Max Runtime (min): Optional — minutes for INTERVAL SLA; comma-separate one value per SLA slot (e.g., 30,45,60)\nSLA Criteria: Optional — ANY RUN (at least one run must complete in the window), ALL RUNS (every run in the window must complete), SELECTED RUNS (only the selected SLA slots are evaluated), LATEST RUN (the day's latest run; deadline may roll into the next day)`, 'Timezone: CST, SLA Type: Daily, SLA Days: Mon,Tue,Wed,Thu,Fri, SLA Start: 08:00, SLA End: 18:00, Criteria: ANY RUN\nMultiple SLA slots (cyclic job): SLA Start: 06:00,14:00, SLA End: 10:00,18:00, Criteria: ALL RUNS'],
      ['6. Optional Fields', 'Non-Mandatory Fields', 'GDO Description/Label, GDO Dependent On, Stage Inbound/Outbound, Data Owner, Data Steward', `Stage Inbound/Outbound: Inbound, Outbound, Both\nGDO Dependent On: comma-separated GDO names (e.g., GDO-001,GDO-A1B). Use NONE to clear all dependencies.`],
      ['7. Remarks / Error Flag Column', 'Error Messages', `The Remarks / Error Flag column appears ONLY in the invalid entries template (not in the default template).\n• It is the first column in the invalid entries template\n• Shows error reasons directly with column names (e.g., "Sector *: Invalid sector", "Market / Country *: Invalid market")\n• If a row has multiple errors, each error appears on a SEPARATE LINE\n• For skipped rows, shows "Manually skipped"\n• Grey, locked, system-populated — do not edit\n• Word wrap is enabled for better readability\n• When re-uploading, this column is automatically ignored by the parser\n• Use this column to understand what went wrong and fix the data`, `Single error: Sector *: Invalid sector\nMultiple errors:\n  Sector *: Invalid sector\n  Market / Country *: Invalid market\nSkipped row → Shows "Manually skipped"`],
      ['8. After Upload - Summary & Errors Sheet', 'Post-Upload Review', `After clicking "Confirm and Upload", a new sheet "Summary & Errors" is generated with:\n• ✅ Successfully Uploaded - Green: Successfully created GDOs\n• ❌ Not Uploaded (Errors) - Red: Failed validations with reasons\n• ⏭ Skipped (Deselected) - Amber: Valid but unchecked rows\n• Download Invalid Entries Template - Orange button to download failed rows as template for correction`, 'Review the "Not Uploaded (Errors)" section, fix issues, download template, correct, re-upload'],
      ['9. Error Resolution Workflow', 'Fixing Errors', `1. Review "Not Uploaded (Errors)" rows in Summary sheet\n2. Click "Download Invalid Entries Template" to get pre-filled template\n3. The Remarks column (first column) shows error reasons for each row\n4. If a row has multiple errors, each appears on a separate line with the column name\n5. Fix all errors shown in the Remarks column\n6. Re-upload the corrected file\n7. Repeat until all rows upload successfully`, `Row with 1 error:\n  Sector *: Invalid sector\nRow with multiple errors:\n  Sector *: Invalid sector\n  Market / Country *: Invalid market`],
      ['10. Validation Rules Summary', 'Key Rules', `File structure (do NOT alter):\n• Sheet name must be exactly "GDO data"\n• Headers must be on row 3 (rows 1-2 are blank)\n• Data starts on row 4\n\nField rules:\n• GDO Name: Must start with GDO-<alphanumeric> (e.g., GDO-001, GDO-A1B), max 64 chars, unique\n• Sector: Must match a value from the Sectors collection\n• Market: Must match a value from the Markets collection\n• Report: Must match a value from the Reports collection\n• Origin, Destination, Platform, Scheduler Name, Job Name: Required, any value\n• Timezone: Must be a valid timezone\n• Schedule Days: Full day names (Monday, Tuesday, etc.), comma-separated, or All\n• Execution Start Time: 24-hr HH:MM (e.g., 06:00, 14:30)\n• At least 2 of Start Time / End Time / Avg Execution Time must be provided\n• SLA Criteria: ANY RUN, ALL RUNS, SELECTED RUNS, or LATEST RUN\n• SLA Start/End Time & SLA Max Runtime (min): single value, or comma-separated for multiple SLA slots per day (paired by position, same count)\n• Remarks column: Only in invalid entries template, shows error/skip reasons (ignored on re-upload)\n• Multiple errors per row: Each error on a separate line with column name`, `Valid sectors: ${sectorList}\nValid markets: ${marketList}\nValid reports: ${reportList}`],
      ['11. Re-upload After Corrections', 'Re-Upload', `1. Download "Invalid Entries Template" from Summary sheet\n2. Fix errors in the downloaded file (Remarks column shows error reasons)\n3. The Remarks column is the first column — do not edit it\n4. Keep all other columns intact\n5. Upload corrected file using "Bulk Upload"\n6. Repeat until 0 errors`, 'Download → Fix → Upload → Verify → Repeat if needed'],
      ['12. Support & Troubleshooting', 'Common Issues', `File structure issues:\n• "Sheet not found" or parse error: Ensure the sheet is named exactly "GDO data" (not "Sheet1", not "GDO Data" with different casing)\n• Headers not recognized: Ensure headers are on row 3 (not row 1 or 2). Rows 1-2 must be blank.\n• Data not appearing in preview: Ensure data starts on row 4 (row 3 must have headers)\n\nField validation issues:\n• "GDO name should start with GDO-<alphanumeric>": Fix prefix to be GDO- followed by alphanumeric characters (e.g., GDO-001, GDO-A1B)\n• "Field is required": Fill in the mandatory field\n• "Schedule Days" validation error: Use full day names (Monday, Tuesday, etc.), not abbreviations\n• "Execution Start Time" validation error: Use 24-hour HH:MM format (e.g., 06:00, 14:30)\n• Platform must be entered as "ADF" (not "Azure Data Factory")`, 'Error: "Sheet not found" → Check sheet name is exactly "GDO data"\nError: "Headers not recognized" → Ensure headers are on row 3'],
    ];

    const wsGuide = XLSX.utils.aoa_to_sheet([guideHeaders, ...guideData]);

    // Style the guide sheet
    const guideHeaderStyle = {
      fill: { fgColor: { rgb: '003771' } },
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      alignment: { horizontal: 'center', wrapText: true, vertical: 'center' },
    };

    for (let c = 0; c < guideHeaders.length; c++) {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c });
      if (wsGuide[XLSX.utils.encode_cell({ r: 0, c })]) {
        wsGuide[XLSX.utils.encode_cell({ r: 0, c })].s = {
          fill: { fgColor: { rgb: '003771' } },
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          alignment: { horizontal: 'center', wrapText: true, vertical: 'center' },
        };
      }
    }

    // Alternate row colors for readability
    for (let r = 1; r <= guideData.length; r++) {
      for (let c = 0; c < guideHeaders.length; c++) {
        const cellRef = XLSX.utils.encode_cell({ r, c });
        if (wsGuide[XLSX.utils.encode_cell({ r, c })]) {
          wsGuide[XLSX.utils.encode_cell({ r, c })].s = {
            fill: { fgColor: { rgb: r % 2 === 0 ? 'FFFFFF' : 'F5F8FC' } },
            alignment: { wrapText: true, vertical: 'center' },
            font: { size: 11 },
          };
        }
      }
    }

    // Set column widths for instruction guide
    wsGuide['!cols'] = [
      { wch: 8 },   // Section
      { wch: 25 },  // Details
      { wch: 10 },  // Required
      { wch: 50 },  // Description / Instructions
      { wch: 60 },  // Valid Values / Format
      { wch: 35 },  // Example / Reference
    ];

    // Set row heights for Instruction Guide sheet
    wsGuide['!rows'] = [
      { hpt: 30 },   // Header row
      ...guideData.map(() => ({ hpt: 60 })),
    ];

    wsGuide['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2' };
    wsGuide['!cols'] = [
      { wch: 8 }, { wch: 25 }, { wch: 10 }, { wch: 50 }, { wch: 60 }, { wch: 35 }
    ];

    XLSX.utils.book_append_sheet(workbook, wsGuide, 'Instruction Guide');
    XLSX.utils.book_append_sheet(workbook, ws, IBP_TEMPLATE.sheetName);

    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array', cellStyles: true });
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = IBP_TEMPLATE.fileName;
    a.click();
    URL.revokeObjectURL(url);
  }, [refData.sectors, refData.reports]);

  const handleFileUpload = useCallback(async (files: File[]) => {
    if (!files?.[0]) return;

    const f = files[0];
    setFileErrors([]);

    const fileError = validateFile(f);
    if (fileError) {
      setFileErrors([fileError]);
      return;
    }

    setIsLoading(true);
    try {
      const parsed = await parseXLSXFile(f);
      if (!parsed.length) {
        setFileErrors(['Uploaded file is empty or contains no valid rows.']);
        setIsLoading(false);
        return;
      }

      const normalized = normalizeParsedRows(parsed);
      setRawRows(normalized);
      setFile(f);
    } catch (err) {
      setFileErrors([`Failed to parse file. ${err instanceof Error ? err.message : ''}`.trim()]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (rawRows.length > 0 && !isLoading) {
      validateAllRows(rawRows, refData)
        .then((validated) => {
          setValidatedRows(validated);

          const validIds = new Set(validated.filter((r) => r.isValid).map((r) => r.id));
          setSelectedRowIds(validIds);

          setActiveStep('preview');
        })
        .catch((err) => {
          console.error('Validation failed:', err);
          setFileErrors([`Validation failed. ${err instanceof Error ? err.message : ''}`.trim()]);
        });
    }
  }, [rawRows, refData, isLoading]);

  const handleRowSelection = useCallback((rowId: number, selected: boolean) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      selected ? next.add(rowId) : next.delete(rowId);
      return next;
    });
  }, []);

  const handleSelectAllValid = useCallback((selected: boolean) => {
    if (selected) {
      const allValid = validatedRows.filter((r) => r.isValid);
      setSelectedRowIds(new Set(allValid.map((r) => r.id)));
    } else {
      setSelectedRowIds(new Set());
    }
  }, [validatedRows]);

  const handleOpenConfirm = useCallback(() => {
    if (selectedRowIds.size === 0) return;
    setConfirmOpen(true);
  }, [selectedRowIds.size]);

  const handleConfirmUpload = useCallback(async () => {
    setConfirmOpen(false);
    setUploading(true);
    setUploadProgress(`Uploading ${selectedRowIds.size} GDOs… please wait.`);

    try {
      const selectedRows = validatedRows.filter((r) => selectedRowIds.has(r.id));

      // Build upload payload — spread all row fields, uppercase sector/market/report for consistent DB casing
      const uploadPayload = selectedRows.map((r) => {
        const { id, isValid, cellErrors, rowError, ...rest } = r;
        return {
          ...rest,
          sector: r.sector.toUpperCase(),
          market: r.market.toUpperCase(),
          report: r.report.toUpperCase(),
        };
      });

      console.log('=== GDO Bulk Upload - Selected Rows for Upload ===', {
        timestamp: new Date().toISOString(),
        count: uploadPayload.length,
        rows: uploadPayload,
      });

      const uploadResponse = await bulkUploadDataPipelineEntities({
        businessProgram: "IBP",
        data: uploadPayload
      })

      // Use the real backend response to build the summary
      const resp = uploadResponse?.data?.data || uploadResponse?.data || uploadResponse || {};
      const backendSummary = resp.summary || {};
      const backendFailed = resp.failed || [];
      const backendDeps = resp.dependencies || {};

      const successCount = backendSummary.successful || 0;
      const failCount = backendSummary.failed || 0;

      // Map backend failed items to error rows
      const errorRows = backendFailed.map((f: any) => {
        const matchedRow = selectedRows.find((r) => r.gdoName === f.gdoName);
        return {
          row: matchedRow || { gdoName: f.gdoName || 'Unknown' },
          reason: f.error || f.reason || 'Upload failed',
        };
      });

      // Collect all client-side validation error rows (not selected or invalid)
      const clientErrorRows = validatedRows
        .filter((r) => selectedRowIds.has(r.id) && !r.isValid)
        .map((r) => ({
          row: r,
          reason: r.remarks || Object.values(r.cellErrors).filter(Boolean).join('\n') || r.rowError || 'Validation failed',
        }));

      const unselectedInvalidRows = validatedRows
        .filter((r) => !r.isValid && !selectedRowIds.has(r.id))
        .map((r) => ({
          row: r,
          reason: r.remarks || Object.values(r.cellErrors).filter(Boolean).join('\n') || r.rowError || 'Validation failed',
        }));

      const allErrorRows = [...clientErrorRows, ...unselectedInvalidRows, ...errorRows];
      const skippedRows = validatedRows.filter((r) => r.isValid && !selectedRowIds.has(r.id));

      setUploadSummary({
        successCount,
        errorCount: allErrorRows.length,
        skippedCount: skippedRows.length,
        successRows: selectedRows.filter((r) => r.isValid).slice(0, successCount),
        errorRows: allErrorRows,
        skippedRows,
        dependencySummary: backendDeps.groupsProcessed > 0 ? backendDeps : undefined,
      });

      setActiveStep('summary');
    } catch (err) {
      alert('Upload failed. No changes have been made. Please retry.');
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  }, [selectedRowIds, validatedRows]);

  const handleUploadAnother = useCallback(() => {
    handleReset();
  }, [handleReset]);

  const handleGoToGDOList = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const handleDownloadReport = useCallback(() => {
    if (!uploadSummary) return;
    const blob = generateStatusReport(uploadSummary);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `GDO_Upload_Status_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }, [uploadSummary]);

  const handleDownloadInvalidTemplate = useCallback(() => {
    if (!uploadSummary) return;
    const blob = generateInvalidEntriesTemplate(uploadSummary, refData);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `GDO_Invalid_Entries_Template_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }, [uploadSummary]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      handleFileUpload(acceptedFiles);
    },
    [handleFileUpload]
  );

  const onDropRejected = useCallback(
    (rejections: any[]) => {
      if (rejections.length > 0) {
        const rejectedFile = rejections[0];
        const fileName = rejectedFile.file?.name || 'Unknown file';
        const fileSize = rejectedFile.file?.size;
        const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
        const acceptErrors = rejectedFile.errors || [];
        
        // Format file size for display
        const formatFileSize = (bytes: number): string => {
          if (bytes < 1024) return `${bytes} B`;
          if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
          return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
        };
        
        if (acceptErrors.some((e: any) => e.code === 'file-invalid-type')) {
          setFileErrors([
            `File "${fileName}" has invalid type ".${fileExtension}". Only .xlsx files are accepted. Please download the template and upload the .xlsx file.`,
          ]);
        } else if (acceptErrors.some((e: any) => e.code === 'file-too-large')) {
          const sizeStr = fileSize ? formatFileSize(fileSize) : '';
          setFileErrors([
            `File "${fileName}"${sizeStr ? ` (${sizeStr})` : ''} exceeds the maximum size of 10 MB. Please reduce the file size and try again.`,
          ]);
        } else if (acceptErrors.some((e: any) => e.code === 'too-many-files')) {
          setFileErrors(["Only one file is allowed at a time. Please upload a single .xlsx file."]);
        } else {
          setFileErrors(["File rejected. Please ensure you are uploading a valid .xlsx file under 10 MB."]);
        }
      }
    },
    []
  );

  const acceptMap = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: acceptMap,
    multiple: false,
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10 MB
    disabled: isLoading || uploading,
  });

  // Determine current loading message based on state
  const getLoadingInfo = useCallback(() => {
    if (uploading) {
      return {
        message: 'Uploading to server...',
        subMessage: 'Creating programs, projects, and jobs in the database...',
      };
    }
    if (isLoading) {
      return {
        message: 'Processing file...',
        subMessage: 'Parsing Excel data and validating rows...',
      };
    }
    return { message: '', subMessage: '' };
  }, [uploading, isLoading]);

  const validCount = validatedRows.filter((r) => r.isValid).length;
  const errorCount = validatedRows.length - validCount;
  const canConfirm = selectedRowIds.size > 0;

  const displayRows = useMemo(() => {
    const filtered = showValidOnly ? validatedRows.filter((r) => r.isValid) : validatedRows;
    return buildGroupedRows(filtered);
  }, [validatedRows, showValidOnly]);

  const renderUploadStep = () => (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Typography variant="h6" sx={{ color: '#009BD8', fontWeight: 600, fontSize: '18px', whiteSpace: 'nowrap' }}>
          Upload a File to Import
        </Typography>
        <Tooltip title="Upload an .xlsx file with GDO program data. Only .xlsx files are accepted (max 10 MB) — please use the downloaded template." placement="right">
          <InfoOutlinedIcon sx={{ fontSize: 20, opacity: 0.6, color: '#71758F' }} />
        </Tooltip>
        <Box sx={{ flexGrow: 1, height: '1.5px', backgroundColor: '#B2E3FF', ml: 2 }} />
      </Box>

      <Box {...getRootProps()} sx={{ mb: 3 }}>
        <div
          style={{
            width: '100%',
            minHeight: '260px',
            borderRadius: '4px',
            border: `2px dashed ${isDragActive ? '#009BD8' : '#D2D6E8'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            boxSizing: 'border-box',
            backgroundColor: isDragActive ? 'rgba(0, 155, 216, 0.05)' : '#F8FAFC',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          <input {...getInputProps()} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', maxWidth: '100%' }}>
            <CloudUploadOutlinedIcon style={{ width: '90px', height: '90px', flexShrink: 0, color: '#A0A5BA' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <Typography sx={{ color: '#94A3B8', fontSize: '16px', fontWeight: 500 }}>
                {isDragActive ? 'Drop .xlsx file here' : 'Drop .xlsx file here (max 10 MB)'}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(0, 155, 216, 1)',
                  textDecoration: 'underline',
                  fontWeight: 500,
                  fontSize: '16px',
                }}
              >
                browse files
              </Typography>
            </Box>
          </Box>
        </div>
      </Box>

      {fileErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
          {fileErrors.map((e, i) => (
            <Typography key={i}>{e}</Typography>
          ))}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
        <Button
          startIcon={<FileDownloadIcon sx={{ color: '#009BD8' }} />}
          variant="outlined"
          onClick={handleDownloadTemplate}
          disabled={isLoading}
          sx={{
            fontSize: '14px',
            color: '#009BD8',
            borderColor: '#009BD8',
            backgroundColor: 'white',
            textTransform: 'none',
            px: 4,
            py: 1,
            '&:hover': {
              borderColor: '#009BD8',
              backgroundColor: 'rgba(0, 155, 216, 0.05)',
            },
            '&.Mui-disabled': {
              color: 'rgba(113, 117, 143, 0.5)',
              borderColor: 'rgba(113, 117, 143, 0.5)',
            },
          }}
        >
          Download Sample Files
        </Button>
      </Box>
    </Box>
  );

  const renderPreviewStep = () => {

    return (
      <Grid container spacing={2} sx={{ margin: 0, width: '100%', display: 'flex', flexDirection: 'column', height: '100%' }}>
        {errorCount > 0 && (
          <Grid item xs={12} sx={{ padding: '0px 20px 0px 20px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 2,
                backgroundColor: 'rgba(255, 162, 0, 0.15)',
                borderRadius: '4px',
                color: 'rgba(255, 162, 0, 1)',
              }}
            >
              <WarningIcon style={{ marginRight: '10px' }} />
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
                Unable to read {errorCount} record{errorCount > 1 ? 's' : ''} due to errors. Those records cannot be added.
              </Typography>
            </Box>
          </Grid>
        )}

        <Grid item xs={12} sx={{ padding: '20px 20px 0px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Grid item xs={9}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '22px', marginBottom: '5px' }}>
              <Typography sx={{ color: 'rgba(15, 55, 100, 1)', display: 'flex', alignItems: 'center', fontSize: '18px', fontWeight: 600 }}>
                Records Preview
              </Typography>
              <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(0, 155, 216, 1)', marginLeft: '16px' }} />
            </Box>
          </Grid>
          <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <FormControlLabel
              sx={{
                marginRight: 0,
                '& .MuiFormControlLabel-label': { color: 'rgba(15, 55, 100, 1) !important', fontWeight: 500, marginLeft: '6px' },
                '& .MuiCheckbox-root': { color: 'rgba(15, 55, 100, 1) !important', padding: '3px' },
                '& .MuiCheckbox-root.Mui-disabled': { color: 'rgba(15, 55, 100, 1) !important', opacity: 1 },
              }}
              control={
                <Checkbox
                  checked={showValidOnly}
                  onChange={(e) => setShowValidOnly(e.target.checked)}
                  icon={
                    <Box sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '3px',
                      border: '2px solid rgba(15, 55, 100, 1)',
                      backgroundColor: 'transparent',
                    }} />
                  }
                  checkedIcon={
                    <Box sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '3px',
                      backgroundColor: 'rgba(15, 55, 100, 1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <CheckIcon style={{ width: 10, height: 10, color: 'white' }} />
                    </Box>
                  }
                  sx={{
                    padding: 0,
                    '&.Mui-disabled': { opacity: 1 },
                  }}
                />
              }
              label="Show valid records only"
            />
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ padding: '10px 20px 0px 20px', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            '& .rdg': {
              flex: 1,
              '&::-webkit-scrollbar': { width: '12px', height: '12px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0, 155, 216, 1)',
                borderRadius: '8px',
                border: '3px solid transparent',
                backgroundClip: 'content-box',
              },
              scrollbarColor: 'rgba(0,155,216,1) transparent',
            }
          }}>
            <GDOValidationPreviewTable
              rows={validatedRows}
              showValidOnly={showValidOnly}
              onShowValidOnlyChange={setShowValidOnly}
              selectedRowIds={selectedRowIds}
              onRowSelectionChange={handleRowSelection}
              onSelectAllValid={handleSelectAllValid}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sx={{ padding: '0px 20px 20px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Grid>
            <Button
              component="label"
              variant="outlined"
              startIcon={<FileUploadIcon color='primary' />}
              sx={{
                width: '200px',
                color: 'rgba(0, 155, 216, 1)',
                borderColor: 'rgba(0, 155, 216, 1)',
              }}
            >
              Re-upload File
              <VisuallyHiddenInput
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(event) => {
                  const files = event.target.files ? Array.from(event.target.files) : [];
                  handleFileUpload(files);
                  event.target.value = '';
                }}
              />
            </Button>
          </Grid>
          <Grid>
            <Button
              variant="text"
              onClick={handleClose}
              sx={{
                color: 'rgba(0, 155, 216, 1)',
                mr: 1,
                textTransform: 'none',
                width: '100px',
              }}
            >
              CANCEL
            </Button>
            <Button
              variant="contained"
              onClick={handleOpenConfirm}
              disabled={!canConfirm}
              sx={{
                minWidth: '170px',
                textTransform: 'none',
                backgroundColor: canConfirm ? '#003771' : '#94A3B8',
                color: 'white',
                '&.Mui-disabled': {
                  backgroundColor: '#94A3B8',
                  color: '#E2E8F0',
                },
              }}
            >
              Confirm and Upload
            </Button>
          </Grid>
        </Grid>

        {!canConfirm && (
          <Grid item xs={12} sx={{ padding: '0px 20px 10px 20px' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Please select at least one valid GDO row to proceed.
            </Typography>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderUploadingStep = () => (
    <CSSLoader
      size={64}
      thickness={4}
      message={uploadProgress || 'Uploading...'}
      subMessage="Creating programs, projects, and jobs in the database..."
    />
  );

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '4px',
          backgroundColor: 'rgba(245, 248, 252, 1)',
          minWidth: "60%",
          minHeight: "50%"
        },
      }}
    >
      <DialogTitle
        bgcolor="primary.main"
        sx={{ p: '20px 30px !important', backgroundColor: '#003771 !important' }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Typography sx={{ fontWeight: 600, color: 'white', fontSize: '24px', whiteSpace: 'nowrap' }}>
            Bulk Upload GDOs
          </Typography>
          <Box display="flex" alignItems="center" gap={1} flex={1}>
            <Stepper
              activeStep={getStepIndex(activeStep)}
              style={{ width: '500px', justifyContent: 'end', padding: '0px', marginLeft: 'auto' }}
              connector={<StyledConnector completed={activeStep !== 'upload'} />}
            >
              {steps.map((label, index) => {
                const isActive = index === getStepIndex(activeStep);
                return (
                  <Step key={label}>
                    <StepLabel StepIconComponent={getStepIconComponent(index, getStepIndex(activeStep))}>
                      <StyledLabel active={isActive} completed={getStepIndex(activeStep) > index}>
                        {label}
                      </StyledLabel>
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
            <IconButton aria-label="Close dialog" onClick={handleClose} sx={{ p: 0 }}>
              <CloseIcon fontSize="medium" sx={{ color: '#ffffff' }} />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, px: 2, backgroundColor: 'rgba(245, 248, 252, 1)', overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'scroll', minHeight: 0 }}>
          {(isLoading || uploading) && (
            <CSSLoader
              size={64}
              thickness={4}
              message={getLoadingInfo().message}
              subMessage={getLoadingInfo().subMessage}
            />
          )}
          {!isLoading && !uploading && activeStep === 'upload' && renderUploadStep()}
          {!isLoading && !uploading && activeStep === 'preview' && renderPreviewStep()}
          {!isLoading && !uploading && activeStep === 'uploading' && renderUploadingStep()}
          {!isLoading && !uploading && activeStep === 'summary' && uploadSummary && (
            <GDOStatusSummary
              summary={uploadSummary}
              onDownloadReport={handleDownloadReport}
              onDownloadInvalidTemplate={handleDownloadInvalidTemplate}
              onGoToGDOList={handleGoToGDOList}
              onUploadAnother={handleUploadAnother}
            />
          )}
        </Box>
      </DialogContent>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Bulk Upload</DialogTitle>
        <DialogContent sx={{ backgroundColor: 'rgba(245, 248, 252, 1)', overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
          <DialogContentText sx={{ p: 2 }}>
            Are you sure you want to upload all selected entries? This will create/update {selectedRowIds.size} GDO(s) in PepWise.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{backgroundColor: 'rgba(245, 248, 252, 1)'}}>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmUpload}
            disabled={uploading}
            sx={{
              backgroundColor: THEME_PRIMARY,
              '&:hover': { backgroundColor: '#002855' },
              '&.Mui-disabled': { backgroundColor: '#94A3B8', color: '#E2E8F0' },
            }}
          >
            {uploading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: 'white',
                    animation: 'ibp-spin 1s linear infinite',
                  }}
                />
                Uploading...
              </Box>
            ) : 'Confirm and Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

function getStepIndex(step: ModalStep): number {
  switch (step) {
    case 'upload': return 0;
    case 'preview': return 1;
    case 'confirm': return 1;
    case 'uploading': return 1;
    case 'summary': return 1;
    default: return 0;
  }
}

function buildGroupedRows(rows: ValidatedGDORow[]): DisplayRow[] {
  const result: DisplayRow[] = [];
  let currentGdoName: string | null = null;

  for (const row of rows) {
    const gdoName = row.gdoName.trim();
    if (gdoName !== currentGdoName) {
      currentGdoName = gdoName;
      const groupChildren = rows.filter((r) => r.gdoName.trim() === gdoName);
      result.push({
        type: 'group',
        id: `group-${gdoName}`,
        gdoName,
        children: groupChildren,
      });
    }
    result.push({ type: 'data', id: row.id, data: row });
  }
  return result;
}

export default GBulkUploadModal;
